import axios from 'axios';
import { UseCase, ProcessResponse, AllUseCasesResponse } from '../types';
import { authService, LoginRequest, LoginResponse } from './auth';

const API_BASE_URL = import.meta.env.REACT_APP_API_URL || 'http://localhost:8000'; // Adjust this to match your backend URL

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const authHeaders = authService.getAuthHeader();
    if ('Authorization' in authHeaders) {
      config.headers.Authorization = authHeaders.Authorization;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor to handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      authService.logout();
    }
    return Promise.reject(error);
  }
);

export const apiService = {
  async listSessions(): Promise<{ confluence_id: string; state: string; updated_at: string }[]> {
    const response = await api.get('/sessions');
    return response.data;
  },

  async deleteSession(confluenceId: string): Promise<void> {
    await api.delete(`/sessions/${confluenceId}`);
  },


  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await api.post('/login', credentials);
    return response.data;
  },

  async getNextUseCase(sessionId: string): Promise<UseCase> {
    const response = await api.get('/next-use-case', {
      params: { session_id: sessionId },
    });
    return response.data;
  },

  async getAllUseCases(sessionId: string): Promise<AllUseCasesResponse> {
    const response = await api.get(`/get-all-use-cases/${sessionId}`);
    return response.data;
  },

  async processUseCase(useCaseText: string, manualType: string, useCaseIdx: number): Promise<ProcessResponse> {
    const response = await api.post('/process-use-case', { 
      use_case_text: useCaseText, 
      manual_type: manualType,
      use_case_idx: useCaseIdx,
    });
    return response.data;
  },

  async confirmChange(
    accepted: boolean,
    originalText: string,
    updatedText: string,
    manualType: string,
    useCaseIdx: number,
    itemIdx: number,
    modifiedText?: string
  ): Promise<{ message: string; manual_text: string }> {
    const response = await api.post('/confirm-change', {
      accepted,
      original_text: originalText,
      updated_text: updatedText,
      modified_text: modifiedText,
      manual_type: manualType,
      use_case_idx: useCaseIdx,
      item_idx: itemIdx,
    });
    return response.data;
  },

  async getProposals(manualType: string, useCaseIdx: number): Promise<{ proposals: any[] }> {
    const response = await api.get(`/proposals/${manualType}/${useCaseIdx}`);
    return response.data;
  },

  async getCurrentManualText(manualType: string): Promise<{ manual_text: string }> {
    const response = await api.get(`/current-manual/${manualType}`);
    return response.data;
  },

  async getManualContent(manualType: string): Promise<{ content: string }> {
    const response = await api.get(`/manuals/${manualType}`);
    return response.data;
  },

  async downloadManual(manualType: string): Promise<Blob> {
    const response = await api.get(`/download-manual/${manualType}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  async getOriginalDocx(manualType: string): Promise<Blob> {
    const response = await api.get(`/manuals/${manualType}/original-docx`, {
      responseType: 'blob',
    });
    return response.data;
  },

  async uploadManual(confluenceId: string, file: File): Promise<{ manual_type: string; filename: string }> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/sessions/${confluenceId}/upload-manual`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  async getUploadedManuals(confluenceId: string): Promise<{ uploaded: string[] }> {
    const response = await api.get(`/sessions/${confluenceId}/uploaded-manuals`);
    return response.data;
  },
};
